import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eyebrow, MaskLine, Reveal } from "@/components/site/Reveal";
import { DetailsLink, Highlights, PurchaseBox } from "@/components/site/ProductBits";
import { useProducts } from "@/hooks/useProducts";

function SawRow({ p, index, onQuote, products }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const flip = index % 2 === 1;
  const keySpecs = p.slug === "battery-concrete-breaker" ? (p.specs || []).filter((s) => ["Output Power", "Rated Voltage", "Impact Energy"].includes(s[0])) : (p.specs || []).slice(0, 3);

  return (
    <div ref={ref} data-testid={`saw-row-${p.slug}`} className="grid items-center gap-8 border-t border-white/10 py-14 md:py-20 lg:grid-cols-12 lg:gap-14">
      <Reveal className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
        <div className="clip-notch group relative aspect-[4/3] overflow-hidden bg-black border border-white/10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(212,230,10,0.18),transparent_55%)]" />
          <motion.img
            style={{ y }}
            src={p.image}
            alt={`${p.name} ${p.model}`}
            className="absolute inset-0 h-full w-full object-contain p-8 md:p-12 mix-blend-lighten transition-transform duration-700 group-hover:scale-[1.04]"
          />
          <span className="absolute left-5 top-5 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-300">{p.model}</span>
          <span className="absolute bottom-4 right-6 font-display text-7xl md:text-9xl font-black text-white/5">0{index + 1}</span>
        </div>
      </Reveal>
      <div className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-rescue">0{index + 1} / {p.kind_label || "Saw"}</p>
        <h3 className="mt-3 font-display text-5xl md:text-7xl font-black uppercase leading-[0.9]" style={{ fontWeight: 900 }}>
          <MaskLine inView>{p.name}</MaskLine>
        </h3>
        <Reveal delay={0.1}>
          <p className="mt-5 font-display text-2xl md:text-3xl uppercase text-volt">{p.tagline}</p>
          <p className="mt-4 text-base text-zinc-300 leading-relaxed max-w-lg">{p.description}</p>
          <dl className="mt-8 grid grid-cols-3 border-y border-white/10">
            {keySpecs.map(([k, v]) => (
              <div key={k} className="py-4 pr-3 border-r border-white/10 last:border-r-0 pl-3 first:pl-0">
                <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{k}</dt>
                <dd className="mt-1 font-display text-xl md:text-2xl font-bold">{v}</dd>
              </div>
            ))}
          </dl>
          <Highlights product={p} className="mt-6" />
          <PurchaseBox product={p} products={products} onQuote={onQuote} extra={<DetailsLink slug={p.slug} />} />
        </Reveal>
      </div>
    </div>
  );
}

export function Arsenal({ onQuote }) {
  const { products, isLoading } = useProducts();
  const saws = products.filter((p) => p.category === "saw");
  return (
    <section id="arsenal" data-testid="arsenal-section" className="relative bg-obsidian py-24 md:py-36">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-10 md:mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Eyebrow>The Arsenal</Eyebrow>
            <h2 className="mt-5 font-display text-[12.5vw] sm:text-6xl md:text-8xl lg:text-9xl font-black uppercase leading-[0.85]" style={{ fontWeight: 900 }}>
              <MaskLine inView>Cut. Breach.</MaskLine>
              <MaskLine inView delay={0.1}><span className="text-outline-volt">No pull cords.</span></MaskLine>
            </h2>
          </div>
          <Reveal className="max-w-sm text-zinc-300 text-base">Squeeze and cut. Battery-powered rescue tools engineered to outperform the gas engine.</Reveal>
        </div>
        {isLoading && <p className="font-mono text-sm text-zinc-400">Loading arsenal…</p>}
        {saws.map((p, i) => (
          <SawRow key={p.slug} p={p} index={i} onQuote={onQuote} products={products} />
        ))}
      </div>
    </section>
  );
}
